import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, ComboboxButton } from "@headlessui/react";
import { startOfDay, parseISO } from "date-fns";
import { XIcon } from "lucide-react";
import { type MyLatestReminderList, type User } from "../types";
import { fakeUsers, matters as matterList } from "../../mocks/calendarMocks";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/components/ui/use-toast";

import { useNavigate, useSearchParams } from "react-router";

export interface TeamCalendarContainerProps {
  eventsAndReminders: MyLatestReminderList["reminderList"];
  onAddSubtask: () => void;
  onSetReminder: () => void;
  onEditEvent: () => void;
  onDeleteEvent: () => void;
  onSelectDate: (date: Date | null, events: MyLatestReminderList["reminderList"]) => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const UserFilter: React.FC<{
  selectedUsers: Array<User>;
  setSelectedUsers: React.Dispatch<React.SetStateAction<Array<User>>>;
}> = ({ selectedUsers, setSelectedUsers }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const comboboxRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return fakeUsers;
    return fakeUsers.filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setSelectedUsers(prevSelectedUsers => prevSelectedUsers.filter(user => user.id !== userId));
    },
    [setSelectedUsers],
  );

  return (
    <div className="flex items-center">
      <Combobox multiple value={selectedUsers} onChange={setSelectedUsers} onClose={() => setSearchQuery("")}>
        <div className="relative mr-2" ref={comboboxRef}>
          <ComboboxInput
            className="w-72 rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            displayValue={(users: User[]) => users.map(user => user.name).join(", ")}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search and select users"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </ComboboxButton>

          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredUsers.length === 0 && searchQuery !== "" ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">No users found.</div>
            ) : (
              filteredUsers.map(user => (
                <ComboboxOption
                  key={user.id}
                  value={user}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-10 pr-4",
                      active ? "bg-indigo-600 text-white" : "text-gray-900",
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span className={classNames("block truncate", selected ? "font-medium" : "font-normal")}>
                        {user.name}
                      </span>
                      {selected ? (
                        <span
                          className={classNames(
                            "absolute inset-y-0 left-0 flex items-center pl-3",
                            active ? "text-white" : "text-indigo-600",
                          )}
                        >
                          <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 00-1.414 0L7 13.586l-3.293-3.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l9-9a1 1 0 000-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      <div className="mt-2 flex flex-wrap">
        {selectedUsers.map(user => (
          <div key={user.id} className="mb-2 mr-2 flex items-center rounded-full bg-gray-200 px-3 py-1">
            <img
              className="inline-block h-5 w-5 rounded-full ring-2 ring-white"
              src={user.avatar}
              alt={user.name}
              width={20}
              height={20}
            />
            <span className="ml-2 text-sm text-gray-700">{user.name}</span>
            <XIcon className="ml-2 h-4 w-4 cursor-pointer text-gray-600" onClick={() => handleRemoveUser(user.id)} />
          </div>
        ))}
      </div>
    </div>
  );
};

const MatterFilter: React.FC<{
  selectedMatters: Array<string>;
  setSelectedMatters: React.Dispatch<React.SetStateAction<Array<string>>>;
}> = ({ selectedMatters, setSelectedMatters }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const comboboxRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredMatters = useMemo(() => {
    if (!searchQuery) return matterList;
    return matterList.filter(
      matter =>
        matter.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        matter.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery]);

  const handleRemoveMatter = useCallback(
    (matterId: string) => {
      setSelectedMatters(prevSelectedMatters => prevSelectedMatters.filter(id => id !== matterId));
    },
    [setSelectedMatters],
  );

  return (
    <div className="flex items-center">
      <Combobox
        multiple
        value={selectedMatters}
        onChange={values => {
          if (values.length <= 2) {
            setSelectedMatters(values);
          }
        }}
        onClose={() => setSearchQuery("")}
      >
        <div className="relative mr-2" ref={comboboxRef}>
          <ComboboxInput
            className="w-72 rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            displayValue={(matters: string[]) =>
              matters
                .map(id => {
                  const matter = matterList.find(m => m.id === id);
                  return matter ? matter.title : id;
                })
                .join(", ")
            }
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search and select matter IDs"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </ComboboxButton>

          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredMatters.length === 0 && searchQuery !== "" ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">No matters found.</div>
            ) : (
              filteredMatters.map(matter => (
                <ComboboxOption
                  key={matter.id}
                  value={matter.id}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-10 pr-4",
                      active ? "bg-indigo-600 text-white" : "text-gray-900",
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span className={classNames("block truncate", selected ? "font-medium" : "font-normal")}>
                        {matter.title}
                      </span>
                      {selected ? (
                        <span
                          className={classNames(
                            "absolute inset-y-0 left-0 flex items-center pl-3",
                            active ? "text-white" : "text-indigo-600",
                          )}
                        >
                          <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5 9a1 1 0 011-1h8a1 1 0 010 2H6a1 1 0 01-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      <div className="mt-2 flex flex-wrap">
        {selectedMatters.map(matterId => (
          <div key={matterId} className="mb-2 mr-2 flex items-center rounded-full bg-gray-200 px-3 py-1">
            <span className="ml-2 text-sm text-gray-700">
              {matterList.find(matter => matter.id === matterId)?.title || matterId}
            </span>
            <XIcon className="ml-2 h-4 w-4 cursor-pointer text-gray-600" onClick={() => handleRemoveMatter(matterId)} />
          </div>
        ))}
      </div>
    </div>
  );
};

const TeamCalendarContainer: React.FC<TeamCalendarContainerProps> = ({ eventsAndReminders, onSelectDate }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedUsers, setSelectedUsers] = useState<Array<User>>([]);
  const [selectedMatters, setSelectedMatters] = useState<Array<string>>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [, setEventsForSelectedDate] = useState<MyLatestReminderList["reminderList"]>([]);

  useEffect(() => {
    const matterIdParams = searchParams.getAll("matterId");
    if (matterIdParams.length) {
      setSelectedMatters(matterIdParams);
    }

    const showToastParam = searchParams.get("showToast");
    if (showToastParam === "true") {
      toast({
        description: "Your reminders has been set",
        title: "Congratulions",
        variant: "success",
      });
    }
  }, [searchParams, navigate, toast]);

  const filteredEventsAndReminders = useMemo(() => {
    const selectedUserIds = selectedUsers.map(user => user.id);
    return eventsAndReminders
      .filter(item => {
        const userMatch =
          selectedUsers.length === 0 || item.assignedUserIds.some(userId => selectedUserIds.includes(userId));
        const matterMatch = selectedMatters.length === 0 || selectedMatters.includes(item.matterId);
        const searchMatch =
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.assignedUserIds.some(userId => {
            const user = fakeUsers.find(u => u.id === userId);
            return user ? user.name.toLowerCase().includes(searchQuery.toLowerCase()) : false;
          }) ||
          matterList
            .find(matter => matter.id === item.matterId)
            ?.title.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          false;
        return userMatch && matterMatch && searchMatch;
      })
      .sort((a, b) => {
        const dateA = new Date(a.datetime);
        const dateB = new Date(b.datetime);
        if (dateA !== dateB) return dateB.getTime() - dateA.getTime();
        return b.assignedUserIds.length - a.assignedUserIds.length;
      });
  }, [eventsAndReminders, selectedUsers, selectedMatters, searchQuery]);

  const handleClearFilters = () => {
    setSelectedUsers([]);
    setSelectedMatters([]);
    setSearchQuery("");
    // Clear matterId parameter from the URL
  };

  const handleDayClick = (date: Date | undefined) => {
    if (!date) return;
    const dateString = startOfDay(date).toISOString().slice(0, 10);
    const eventsForDay = filteredEventsAndReminders.filter(
      event => startOfDay(parseISO(event.datetime)).toISOString().slice(0, 10) === dateString,
    );

    if (selectedDate && dateString === startOfDay(selectedDate).toISOString().slice(0, 10)) {
      setSelectedDate(null);
      setEventsForSelectedDate([]);
      onSelectDate(null, []);
    } else {
      setSelectedDate(date);
      setEventsForSelectedDate(eventsForDay);
      onSelectDate(date, eventsForDay);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <UserFilter selectedUsers={selectedUsers} setSelectedUsers={setSelectedUsers} />
        <MatterFilter selectedMatters={selectedMatters} setSelectedMatters={setSelectedMatters} />
        <div className="relative">
          <input
            type="text"
            className="w-72 rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            placeholder="Search by title, summary, user, or matter"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className="ml-4 rounded-md bg-red-600 px-4 py-2 text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          onClick={handleClearFilters}
        >
          Clear All Filters
        </button>
      </div>
      <div className="grid grid-cols-3 gap-4 p-4">
        {new Array(6).fill(null).map((_, index) => (
          <Calendar
            key={index}
            month={new Date(new Date().getFullYear(), new Date().getMonth() + index, 1)}
            onSelect={handleDayClick}
            mode="single"
            selected={selectedDate || undefined}
            modifiers={{
              hasEvents: day =>
                day &&
                filteredEventsAndReminders.some(
                  event =>
                    startOfDay(parseISO(event.datetime)).toISOString().slice(0, 10) ===
                    startOfDay(day).toISOString().slice(0, 10),
                ),
              multipleEvents: day =>
                day
                  ? filteredEventsAndReminders.filter(
                      event =>
                        startOfDay(parseISO(event.datetime)).toISOString().slice(0, 10) ===
                        startOfDay(day).toISOString().slice(0, 10),
                    ).length > 1
                  : false,
              today: day =>
                day
                  ? startOfDay(day).toISOString().slice(0, 10) === startOfDay(new Date()).toISOString().slice(0, 10)
                  : false,
              selected: day =>
                selectedDate && day
                  ? startOfDay(day).toISOString().slice(0, 10) === startOfDay(selectedDate).toISOString().slice(0, 10)
                  : false,
            }}
            modifiersClassNames={{
              hasEvents:
                'after:content-["•"] after:block after:absolute after:bottom-0 after:translate-y-2 after:left-1/2 after:-translate-x-1/2 after:text-green-500 after:text-md',
              multipleEvents:
                'after:content-["••"] after:block after:absolute after:bottom-0 after:translate-y-2 after:left-1/2 after:-translate-x-1/2 after:text-green-500 after:text-md',
              selected: "bg-blue-500 text-white",
              today: "bg-blue-200",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default TeamCalendarContainer;
